import { database } from '../config/backend';
import { ref, get as rtdbGet, set as rtdbSet, push, query, orderByChild, equalTo, remove as rtdbRemove } from './phpDataAdapter';
import { ManipulatedFormula, FormulaTemplate, Ingredient } from './mockData';

/**
 * Remove propriedades undefined de um objeto
 */
function removeUndefined(obj: any): any {
  if (obj === null || obj === undefined) {
    return obj;
  }
  if (Array.isArray(obj)) {
    return obj.map(removeUndefined);
  }
  if (typeof obj === 'object') {
    const cleaned: any = {};
    for (const key in obj) {
      if (obj[key] !== undefined) {
        cleaned[key] = removeUndefined(obj[key]);
      }
    }
    return cleaned;
  }
  return obj;
}

/**
 * Cria uma nova prescrição de fórmula manipulada
 */
export async function createManipulatedFormula(
  nutritionistId: string,
  patientId: string,
  formula: Omit<ManipulatedFormula, 'id' | 'patientId' | 'nutritionistId' | 'createdAt' | 'prescribedAt'>
): Promise<string> {
  try {
    // Validação dos campos obrigatórios
    if (!nutritionistId || !patientId) {
      throw new Error('ID do nutricionista e paciente são obrigatórios');
    }
    if (!formula.formulaName || !formula.formulaName.trim()) {
      throw new Error('Nome da fórmula é obrigatório');
    }
    if (!formula.ingredients || formula.ingredients.length === 0) {
      throw new Error('A fórmula deve conter pelo menos um ingrediente');
    }

    // Usar estrutura users/{nutritionistId}/manipulatedFormulas para consistência
    const formulaRef = push(ref(database, `users/${nutritionistId}/manipulatedFormulas`));
    const formulaData: ManipulatedFormula = {
      id: formulaRef.key as string,
      ...formula,
      nutritionistId,
      patientId,
      createdAt: new Date().toISOString(),
      prescribedAt: new Date().toISOString(),
      status: formula.status || 'active',
    };

    // Remove valores undefined antes de salvar (JSON não serializa campos indefinidos)
    const cleanedData = removeUndefined(formulaData);

    await rtdbSet(formulaRef, cleanedData);
    return formulaRef.key as string;
  } catch (error: any) {
    console.error('Erro ao criar fórmula manipulada:', error);
    const errorMessage = error?.message || 'Não foi possível criar a prescrição';
    throw new Error(errorMessage);
  }
}

/**
 * Busca prescrições de fórmulas manipuladas
 */
export async function getManipulatedFormulas(
  nutritionistId: string,
  patientId?: string
): Promise<ManipulatedFormula[]> {
  try {
    const formulasRef = ref(database, `users/${nutritionistId}/manipulatedFormulas`);
    const snapshot = await rtdbGet(formulasRef);

    if (!snapshot.exists()) {
      return [];
    }

    const data = snapshot.val();
    const formulas: ManipulatedFormula[] = Object.entries(data)
      .map(([id, formula]: [string, any]) => ({
        id,
        ...formula,
      }))
      .filter((formula: ManipulatedFormula) => {
        // Já filtrado por nutritionistId no caminho, mas manter validação
        if (formula.nutritionistId !== nutritionistId) return false;
        if (patientId && formula.patientId !== patientId) return false;
        return true;
      })
      .sort((a, b) => new Date(b.prescribedAt).getTime() - new Date(a.prescribedAt).getTime());

    return formulas;
  } catch (error) {
    console.error('Erro ao buscar fórmulas manipuladas:', error);
    return [];
  }
}

/**
 * Atualiza uma prescrição de fórmula manipulada
 */
export async function updateManipulatedFormula(
  nutritionistId: string,
  formulaId: string,
  updates: Partial<ManipulatedFormula>
): Promise<void> {
  try {
    const formulaRef = ref(database, `users/${nutritionistId}/manipulatedFormulas/${formulaId}`);
    const cleanedUpdates = removeUndefined({
      ...updates,
      updatedAt: new Date().toISOString(),
    });
    await rtdbSet(formulaRef, cleanedUpdates);
  } catch (error) {
    console.error('Erro ao atualizar fórmula manipulada:', error);
    throw new Error('Não foi possível atualizar a prescrição');
  }
}

/**
 * Cancela uma prescrição de fórmula manipulada
 */
export async function deleteManipulatedFormula(nutritionistId: string, formulaId: string): Promise<void> {
  try {
    const formulaRef = ref(database, `users/${nutritionistId}/manipulatedFormulas/${formulaId}`);
    await rtdbSet(formulaRef, {
      status: 'cancelled',
      cancelledAt: new Date().toISOString(),
    });
  } catch (error) {
    console.error('Erro ao cancelar fórmula manipulada:', error);
    throw new Error('Não foi possível cancelar a prescrição');
  }
}

/**
 * Salva uma fórmula como template
 */
export async function saveTemplate(
  nutritionistId: string,
  template: Omit<FormulaTemplate, 'id' | 'nutritionistId' | 'createdAt'>
): Promise<string> {
  try {
    const templateRef = push(ref(database, `manipulatedTemplates/${nutritionistId}`));
    const templateData: FormulaTemplate = {
      id: templateRef.key as string,
      ...template,
      nutritionistId,
      createdAt: new Date().toISOString(),
    };

    await rtdbSet(templateRef, templateData);
    return templateRef.key as string;
  } catch (error) {
    console.error('Erro ao salvar template:', error);
    throw new Error('Não foi possível salvar o template');
  }
}

/**
 * Busca templates de fórmulas
 */
export async function getTemplates(nutritionistId: string): Promise<FormulaTemplate[]> {
  try {
    const templatesRef = ref(database, `manipulatedTemplates/${nutritionistId}`);
    const snapshot = await rtdbGet(templatesRef);

    if (!snapshot.exists()) {
      return [];
    }

    const data = snapshot.val();
    const templates: FormulaTemplate[] = Object.entries(data)
      .map(([id, template]: [string, any]) => ({
        id,
        ...template,
      }))
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

    return templates;
  } catch (error) {
    console.error('Erro ao buscar templates:', error);
    return [];
  }
}

/**
 * Deleta um template
 */
export async function deleteTemplate(nutritionistId: string, templateId: string): Promise<void> {
  try {
    const templateRef = ref(database, `manipulatedTemplates/${nutritionistId}/${templateId}`);
    await rtdbRemove(templateRef);
  } catch (error) {
    console.error('Erro ao deletar template:', error);
    throw new Error('Não foi possível deletar o template');
  }
}

